import React, { Component } from "react";
import {
  Input,
  Modal,
  Collapse,
  TabContent,
  TabPane,
} from "reactstrap";
import {
  addBranchAdmin,
  getBranchList,
  getBranchById,
  createBranch,
  updateBranch,
  toggleBranchStatus,
  deleteBranch,
  updateBranchSettings,
  getMasterCatalog,
  setBranchCatalogSelection,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, StatCard, StatRow, SearchPill, Table, Chip, Btn, IconBtn, Field, Toggle, ConfirmDialog, DialogHead,
  Thumb,
} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

// Keys are the values the Branch schema stores. They must match exactly:
// openEdit looks saved hours up by `h.day === d.key`, so a mismatch would
// silently fall back to defaults and overwrite the branch's real hours on the
// next save. (The API also normalises "mon" -> "monday" defensively.)

// Currency is a fixed list, not free text — a typo here would follow every
// price and payment the branch ever takes. USD first: it is the branch default
// and the currency M-PESA is configured for (MPESA_CURRENCY).

// Fixed lists rather than free text. A typo in any of these is silent and
// long-lived: the timezone drives operating-hours logic, and the currency
// follows every price the branch takes.
const COUNTRIES = [
  "Democratic Republic of Congo",
  "Zambia",
  "Tanzania",
  "Rwanda",
  "Burundi",
  "Uganda",
  "Kenya",
  "South Africa",
];

const TIMEZONES = [
  "Africa/Lubumbashi",
  "Africa/Kinshasa",
  "Africa/Lusaka",
  "Africa/Dar_es_Salaam",
  "Africa/Kigali",
  "Africa/Bujumbura",
  "Africa/Kampala",
  "Africa/Nairobi",
  "Africa/Johannesburg",
];

const CURRENCIES = [
  { code: "USD", label: "US Dollar" },
  { code: "CDF", label: "Congolese Franc" },
  { code: "EUR", label: "Euro" },
  { code: "ZAR", label: "South African Rand" },
];

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

// Check-in / check-out are stored as "HH:mm" strings (Branch schema).
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

// Branch images are stored as URL/path strings — the branch API takes JSON and
// has no file upload — so they are entered as links. Same shapes the upload
// routes store elsewhere: an absolute URL or an "uploads/..." path.
const IMAGE_REF = /^(https?:\/\/|\/|uploads\/)\S+$/i;

const emptyHours = () =>
  DAYS.map((d) => ({
    day: d.key,
    openTime: "09:00",
    closeTime: "23:00",
    isClosed: false,
  }));

const emptyForm = {
  branchId: "",
  code: "",
  name: "",
  description: "",
  address: "",
  city: "",
  country: "Democratic Republic of Congo",
  latitude: "",
  longitude: "",
  timezone: "Africa/Lubumbashi",
  currency: "USD",
  contactPhone: "",
  contactEmail: "",
  isActive: true,
  isDefault: false,
  operatingHours: emptyHours(),
  // Storefront content for the customer app
  coverImage: "",
  images: [],
  newImageUrl: "",
  checkInTime: "",
  checkOutTime: "",
  cancellationPolicy: "",
  freeCancelHours: "",
  // Branch admin (provisioned only on create)
  adminEmail: "",
  adminFirstName: "",
  adminLastName: "",
  adminMobile: "",
};

// Master-catalog pick-list state. Every branch carries every catalog item by
// default (opt-out model) — provisioning a branch means UNTICKING what it does
// not sell.
const emptyCatalog = {
  // Which tab the create/edit modal opens on. Reset with the rest of the form
  // so a previous edit never leaves you on someone else's tab.
  activeTab: "details",
  catalogGroups: [],
  catalogSelection: {}, // { food: { id: true }, roomType: {...}, ... }
  catalogOpenGroups: {}, // { food: true, ... }
  catalogSearch: "",
  catalogLoading: false,
  catalogLoaded: false,
};

const emptySettings = {
  foodDeliveryCharge: "",
  minOrderAmount: "",
  taxRate: "",
  roomBookingEnabled: true,
  tableBookingEnabled: true,
  foodOrderingEnabled: true,
  transportEnabled: true,
  isActive: true,
};


// The master-catalog API returns `price` as an OBJECT keyed by the type's price
// fields (e.g. {amount, discount, finalAmount}), not a scalar. Rendering it
// directly makes React throw "Objects are not valid as a React child", which
// unmounts the modal and blanks the page — so collapse it to a short string and
// return "" when there is nothing worth showing.
const GUEST_PRICE_FIELDS = [
  "finalAmount",
  "total",
  "amount",
  "basePrice",
  "fixedAmount",
];

// Money always reads with two decimals — "$25.20", not "$25.2".
const money = (n) => `$${Number(n).toFixed(2)}`;

const formatItemPrice = (price) => {
  if (price == null) return "";
  // Tolerate a scalar in case the shape ever changes back.
  if (typeof price === "number") return money(price);
  if (typeof price === "string") return price;
  if (typeof price !== "object") return "";

  const key =
    GUEST_PRICE_FIELDS.find((f) => typeof price[f] === "number") ||
    Object.keys(price).find((f) => typeof price[f] === "number");
  if (!key) return "";
  return money(price[key]);
};

/**
 * Scoped CSS for this screen. Inline styles cannot reach reactstrap's modal
 * shell or a native checkbox's tick colour.
 */
const CSS = `
.br-modal .modal-content{border:0;border-radius:22px;background:#fdf9f3;
  box-shadow:0 24px 60px rgba(0,0,0,.35);overflow:hidden}
/* MODAL_CSS uppercases every <label> in a dialog (it is styling field
   captions); these are item names, so they opt back out. */
.con-modal label.br-check{display:flex;align-items:flex-start;gap:9px;padding:5px 0;
  cursor:pointer;font-size:13px;line-height:1.45;color:${C.slate};margin:0;
  text-transform:none;letter-spacing:0}
.br-check input{accent-color:${C.brown};width:16px;height:16px;margin-top:2px;flex:0 0 16px}
.br-tab{padding:8px 15px;border-radius:17px;font-size:14px;cursor:pointer;border:0;
  background:transparent;color:${C.taupe};font-family:inherit;display:inline-flex;
  align-items:center;gap:7px}
.br-tab.is-active{background:${C.brown};color:#fff}
.br-code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:13px;
  letter-spacing:.4px;color:${C.brown};background:${C.iconTint};padding:4px 9px;border-radius:8px}
`;

class BranchList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      search: "",
      // form modal
      modal: false,
      saving: false,
      ...emptyForm,
      // master catalog pick-list (inside the create/edit modal)
      ...emptyCatalog,
      // add-admin modal (existing branches)
      adminModal: false,
      adminBranch: null,
      savingAdmin: false,
      newAdmin: { adminEmail: "", adminFirstName: "", adminLastName: "", adminMobile: "" },
      // settings modal
      settingsModal: false,
      settingsBranchId: "",
      settingsBranchName: "",
      settingsSaving: false,
      settings: { ...emptySettings },
      // confirm modals
      showToggleModal: false,
      showDeleteModal: false,
      selectedBranchId: null,
      selectedBranchStatus: null,
      actionLoading: false,
    };
  }

  componentDidMount() {
    this.getAllBranches();
  }

  getAllBranches = () => {
    getBranchList()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({
            isLoaded: true,
            items: result.data || [],
          });
        } else {
          this.setState({ isLoaded: true });
          toast.error((result && result.message) || "Failed to fetch branches");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error fetching branches");
      });
  };

  visibleBranches() {
    const q = this.state.search.trim().toLowerCase();
    if (!q) return this.state.items;
    return this.state.items.filter((b) =>
      [b.code, b.name, b.city, b.contactEmail, b.contactPhone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }

  columns() {
    const write = canWrite();
    return [
      {
        key: "code",
        label: "Code",
        width: 110,
        render: (b) => <span className="br-code">{b.code}</span>,
      },
      {
        key: "name",
        label: "Branch",
        render: (b) => (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 15, color: C.ink }}>{b.name}</span>
              {b.isDefault && <Chip tone="neutral">Default</Chip>}
            </div>
            {b.description && (
              <div
                title={b.description}
                style={{
                  marginTop: 3, fontSize: 12, color: C.taupeLight, maxWidth: 320,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >
                {b.description}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "city",
        label: "Where",
        width: 190,
        render: (b) => (
          <div>
            <div style={{ fontSize: 14, color: C.ink }}>{b.city || "—"}</div>
            <div style={{ fontSize: 12, color: C.taupeLight }}>{b.country || "—"}</div>
          </div>
        ),
      },
      {
        key: "contact",
        label: "Contact",
        width: 210,
        render: (b) =>
          b.contactPhone || b.contactEmail ? (
            <div>
              {b.contactPhone && <div style={{ fontSize: 14, color: C.ink }}>{b.contactPhone}</div>}
              {b.contactEmail && (
                <div style={{ fontSize: 12, color: C.taupeLight }}>{b.contactEmail}</div>
              )}
            </div>
          ) : (
            <span style={{ color: C.taupeLight }}>—</span>
          ),
      },
      {
        key: "status",
        label: "Status",
        width: 110,
        render: (b) =>
          b.isActive ? <Chip tone="good">Active</Chip> : <Chip tone="bad">Inactive</Chip>,
      },
      // Write controls stay behind canWrite(), exactly as before: a view-only
      // account still sees every branch and its state, just no way to change it.
      ...(write
        ? [
            {
              key: "actions",
              label: "",
              width: 190,
              align: "right",
              render: (b) => (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
                >
                  <IconBtn icon="edit" title="Edit branch" onClick={() => this.openEdit(b._id)} />
                  <IconBtn
                    icon="shield_person"
                    title="Add branch admin"
                    onClick={() => this.openAdminModal(b)}
                  />
                  <IconBtn
                    icon="tune"
                    title="Branch settings"
                    onClick={() => this.openSettings(b._id)}
                  />
                  <IconBtn
                    icon={b.isActive ? "pause" : "play_arrow"}
                    title={b.isActive ? "Deactivate branch" : "Activate branch"}
                    tone="warn"
                    onClick={() => this.handleToggleStatus(b._id, b.isActive)}
                  />
                  <IconBtn
                    icon="delete"
                    tone="bad"
                    disabled={b.isDefault}
                    title={b.isDefault ? "The default branch cannot be deleted" : "Delete branch"}
                    onClick={() => this.handleDelete(b._id, b.isDefault)}
                  />
                </div>
              ),
            },
          ]
        : []),
    ];
  }

  // ---- Create / Edit form modal ----
  toggle = () => {
    this.setState((s) => ({ modal: !s.modal }));
  };

  openCreate = () => {
    this.setState({
      ...emptyForm,
      ...emptyCatalog,
      operatingHours: emptyHours(),
      images: [],
      modal: true,
    });
    // No branchId yet — every item comes back carried:true, so the new branch
    // starts out selling the whole master catalog.
    this.loadCatalog(null);
  };

  openEdit = (id) => {
    getBranchById(id)
      .then((result) => {
        if (result && result.status === 200) {
          const b = result.data.branch || result.data;
          this.setState({
            ...emptyCatalog,
            modal: true,
            branchId: b._id,
            code: b.code || "",
            name: b.name || "",
            description: b.description || "",
            address: b.address || "",
            city: b.city || "",
            country: b.country || "Democratic Republic of Congo",
            latitude:
              b.coordinates && b.coordinates.latitude != null
                ? String(b.coordinates.latitude)
                : "",
            longitude:
              b.coordinates && b.coordinates.longitude != null
                ? String(b.coordinates.longitude)
                : "",
            timezone: b.timezone || "Africa/Lubumbashi",
            currency: b.currency || "USD",
            contactPhone: b.contactPhone || "",
            contactEmail: b.contactEmail || "",
            isActive: b.isActive !== false,
            isDefault: !!b.isDefault,
            coverImage: b.coverImage || "",
            images: Array.isArray(b.images) ? b.images.filter(Boolean) : [],
            newImageUrl: "",
            checkInTime: b.checkInTime || "",
            checkOutTime: b.checkOutTime || "",
            cancellationPolicy: b.cancellationPolicy || "",
            freeCancelHours: b.freeCancelHours == null ? "" : String(b.freeCancelHours),
            operatingHours:
              Array.isArray(b.operatingHours) && b.operatingHours.length
                ? DAYS.map((d) => {
                    const found = b.operatingHours.find((h) => h.day === d.key);
                    return found
                      ? {
                          day: d.key,
                          openTime: found.openTime || "09:00",
                          closeTime: found.closeTime || "23:00",
                          isClosed: !!found.isClosed,
                        }
                      : { day: d.key, openTime: "09:00", closeTime: "23:00", isClosed: false };
                  })
                : emptyHours(),
          });
          // Ticks must reflect what this branch currently carries.
          this.loadCatalog(b._id);
        } else {
          toast.error((result && result.message) || "Failed to load branch");
        }
      })
      .catch(() => toast.error("Error loading branch"));
  };

  // ---- Master catalog pick-list ----
  loadCatalog = (branchId) => {
    this.setState({ catalogLoading: true });
    getMasterCatalog(branchId || undefined)
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const groups = Array.isArray(result.data.groups) ? result.data.groups : [];
          const catalogSelection = {};
          const catalogOpenGroups = {};
          groups.forEach((g) => {
            const picked = {};
            (g.items || []).forEach((it) => {
              // `carried` is computed server-side; with no branchId it is true
              // for everything (opt-out model).
              if (it.carried !== false) picked[it._id] = true;
            });
            catalogSelection[g.key] = picked;
            catalogOpenGroups[g.key] = false;
          });
          this.setState({
            catalogGroups: groups,
            catalogSelection,
            catalogOpenGroups,
            catalogLoading: false,
            catalogLoaded: true,
          });
        } else {
          this.setState({ catalogLoading: false, catalogLoaded: false });
          toast.error((result && result.message) || "Failed to load master catalog");
        }
      })
      .catch(() => {
        this.setState({ catalogLoading: false, catalogLoaded: false });
        toast.error("Error loading master catalog");
      });
  };

  toggleCatalogGroup = (key) => {
    this.setState((s) => ({
      catalogOpenGroups: { ...s.catalogOpenGroups, [key]: !s.catalogOpenGroups[key] },
    }));
  };

  toggleCatalogItem = (key, id) => {
    this.setState((s) => {
      const group = { ...(s.catalogSelection[key] || {}) };
      if (group[id]) delete group[id];
      else group[id] = true;
      return { catalogSelection: { ...s.catalogSelection, [key]: group } };
    });
  };

  // Bulk select/clear. Applies to the items currently visible in this group
  // (i.e. respects the search filter).
  setGroupSelection = (key, selectAll) => {
    const group = this.state.catalogGroups.find((g) => g.key === key);
    if (!group) return;
    const visible = this.filteredItems(group);
    this.setState((s) => {
      const picked = { ...(s.catalogSelection[key] || {}) };
      visible.forEach((it) => {
        if (selectAll) picked[it._id] = true;
        else delete picked[it._id];
      });
      return { catalogSelection: { ...s.catalogSelection, [key]: picked } };
    });
  };

  filteredItems = (group) => {
    const q = this.state.catalogSearch.trim().toLowerCase();
    const items = (group && group.items) || [];
    if (!q) return items;
    return items.filter((it) => String(it.name || "").toLowerCase().indexOf(q) !== -1);
  };

  countSelected = (key) => {
    const picked = this.state.catalogSelection[key] || {};
    return Object.keys(picked).length;
  };

  catalogTotals = () => {
    let selected = 0;
    let total = 0;
    this.state.catalogGroups.forEach((g) => {
      total += (g.items || []).length;
      selected += this.countSelected(g.key);
    });
    return { selected, total };
  };

  // { food: [ids the branch carries], roomType: [...], ... } for every group.
  buildCatalogSelection = () => {
    const selection = {};
    this.state.catalogGroups.forEach((g) => {
      const picked = this.state.catalogSelection[g.key] || {};
      selection[g.key] = (g.items || [])
        .filter((it) => !!picked[it._id])
        .map((it) => it._id);
    });
    return selection;
  };

  handleChange = (e) => {
    this.setState({ [e.target.id]: e.target.value });
  };

  handleHourChange = (index, field, value) => {
    const operatingHours = this.state.operatingHours.map((h, i) =>
      i === index ? { ...h, [field]: value } : h
    );
    this.setState({ operatingHours });
  };

  // ---- Storefront gallery ----
  addGalleryImage = () => {
    const url = this.state.newImageUrl.trim();
    if (!url) return;
    if (!IMAGE_REF.test(url)) {
      toast.error("Enter an image link starting with http:// or https://");
      return;
    }
    if (this.state.images.includes(url)) {
      toast.error("That image is already in the gallery");
      return;
    }
    this.setState((st) => ({ images: [...st.images, url], newImageUrl: "" }));
  };

  removeGalleryImage = (index) => {
    this.setState((st) => ({ images: st.images.filter((_, i) => i !== index) }));
  };

  buildPayload = () => {
    const s = this.state;
    const payload = {
      code: s.code.trim().toUpperCase(),
      name: s.name.trim(),
      description: s.description.trim(),
      address: s.address.trim(),
      city: s.city.trim(),
      country: s.country.trim(),
      timezone: s.timezone.trim(),
      currency: s.currency.trim(),
      contactPhone: s.contactPhone.trim(),
      contactEmail: s.contactEmail.trim(),
      operatingHours: s.operatingHours,
      // Storefront. Every field is sent so clearing one in the form clears it
      // on the branch; freeCancelHours blank = null ("not offered").
      coverImage: s.coverImage.trim(),
      images: s.images,
      checkInTime: s.checkInTime.trim(),
      checkOutTime: s.checkOutTime.trim(),
      cancellationPolicy: s.cancellationPolicy.trim(),
      freeCancelHours: String(s.freeCancelHours).trim() === "" ? null : Number(s.freeCancelHours),
    };
    if (s.latitude !== "" || s.longitude !== "") {
      payload.coordinates = {
        latitude: s.latitude === "" ? null : Number(s.latitude),
        longitude: s.longitude === "" ? null : Number(s.longitude),
      };
    }
    return payload;
  };


  // Tabs for the create/edit modal. Branch Admin only exists while creating —
  // an existing branch's admins are managed under Team, not here.
  tabsForModal = () => {
    const s = this.state;
    const tabs = [
      { key: "details", label: "Details", icon: "info" },
      { key: "hours", label: "Operating hours", icon: "schedule" },
      { key: "storefront", label: "Storefront", icon: "storefront" },
    ];
    if (!s.branchId) {
      tabs.push({
        key: "admin",
        label: "Branch admin",
        icon: "shield_person",
        // Provisioning the manager is only possible while creating, and the
        // tab is easy to skip. Mark it until an email is entered.
        hint: s.adminEmail.trim() ? null : "not set",
      });
    }
    const totals = this.catalogTotals();
    tabs.push({
      key: "catalog",
      label: "Catalog",
      icon: "inventory_2",
      // Surfaced on the tab so the selection is visible from any other tab.
      badge: totals.total ? `${totals.selected}/${totals.total}` : null,
    });
    return tabs;
  };


  // Step navigation across the modal tabs. Create stays available on every
  // tab — most branches are fine on defaults, and forcing four clicks to
  // accept them would be worse than the scrolling this replaced. Next/Back
  // are for people who do want to walk the whole form.
  tabIndex = () => {
    const keys = this.tabsForModal().map((t) => t.key);
    const i = keys.indexOf(this.state.activeTab);
    return i === -1 ? 0 : i;
  };

  goStep = (delta) => {
    const keys = this.tabsForModal().map((t) => t.key);
    const next = this.tabIndex() + delta;
    if (next < 0 || next >= keys.length) return;
    this.setState({ activeTab: keys[next] });
  };

  // A field can fail validation while its tab is hidden. Show the tab first,
  // otherwise the toast points at something the user cannot see.
  failOn = (tab, message) => {
    this.setState({ activeTab: tab });
    toast.error(message);
    return false;
  };

  handleSubmit = () => {
    const { branchId, name, code } = this.state;
    if (!name.trim()) return this.failOn("details", "Branch name is required");
    if (!code.trim()) return this.failOn("details", "Branch code is required");
    const { coverImage, newImageUrl, checkInTime, checkOutTime, freeCancelHours } = this.state;
    if (coverImage.trim() && !IMAGE_REF.test(coverImage.trim())) {
      return this.failOn("storefront", "Cover image must be a link starting with http:// or https://");
    }
    // A link typed but never added would otherwise be dropped silently.
    if (newImageUrl.trim()) {
      return this.failOn("storefront", "Press Add to put the gallery link in, or clear it");
    }
    if (checkInTime.trim() && !HHMM.test(checkInTime.trim())) {
      return this.failOn("storefront", "Check-in time must be HH:mm, e.g. 14:00");
    }
    if (checkOutTime.trim() && !HHMM.test(checkOutTime.trim())) {
      return this.failOn("storefront", "Check-out time must be HH:mm, e.g. 11:00");
    }
    const hours = String(freeCancelHours).trim();
    if (hours !== "" && !(Number.isInteger(Number(hours)) && Number(hours) >= 0)) {
      return this.failOn("storefront", "Free cancellation hours must be a whole number of 0 or more");
    }
    const { adminEmail } = this.state;
    if (!branchId && adminEmail.trim() && !/^\S+@\S+\.\S+$/.test(adminEmail.trim())) {
      return this.failOn("admin", "Enter a valid branch admin email");
    }
    const payload = this.buildPayload();
    const catalogLoaded = this.state.catalogLoaded;
    const catalogSelection = catalogLoaded ? this.buildCatalogSelection() : null;
    this.setState({ saving: true });

    let createPayload = { ...payload, isActive: this.state.isActive };
    if (adminEmail.trim()) {
      createPayload.adminEmail = adminEmail.trim();
      createPayload.adminFirstName = this.state.adminFirstName.trim();
      createPayload.adminLastName = this.state.adminLastName.trim();
      if (this.state.adminMobile.trim())
        createPayload.adminMobile = this.state.adminMobile.trim();
    }
    // On create the pick-list rides along in the same POST body.
    if (!branchId && catalogSelection) createPayload.catalogSelection = catalogSelection;

    const req = branchId
      ? updateBranch(branchId, payload).then((response) => {
          // On edit the pick-list is a separate call, made only once the
          // branch itself saved cleanly.
          if (!(response && response.status === 200) || !catalogSelection) return response;
          return setBranchCatalogSelection(branchId, catalogSelection)
            .then((catRes) => {
              if (!(catRes && catRes.status === 200)) {
                toast.error(
                  (catRes && catRes.message) ||
                    "Branch saved, but the catalog selection could not be updated"
                );
              }
              return response;
            })
            .catch(() => {
              toast.error("Branch saved, but the catalog selection could not be updated");
              return response;
            });
        })
      : createBranch(createPayload);

    req
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(
            branchId
              ? "Branch updated successfully"
              : adminEmail.trim()
              ? response.message || "Branch created and admin credentials emailed"
              : "Branch created successfully"
          );
          this.getAllBranches();
          this.setState({ modal: false });
        } else {
          toast.error((response && response.message) || "Failed to save branch");
        }
      })
      .catch(() => toast.error("Error saving branch"))
      .finally(() => this.setState({ saving: false }));
  };

  // ---- Branch Settings modal ----
  toggleSettings = () => {
    this.setState((s) => ({ settingsModal: !s.settingsModal }));
  };


  // Adding an admin to an EXISTING branch. Creation can provision one inline,
  // but that is a one-shot; without this the only route afterwards is Team,
  // with a password typed by hand instead of generated and emailed.
  openAdminModal = (branch) => {
    this.setState({
      adminModal: true,
      adminBranch: branch,
      newAdmin: { adminEmail: "", adminFirstName: "", adminLastName: "", adminMobile: "" },
      savingAdmin: false,
    });
  };

  closeAdminModal = () => this.setState({ adminModal: false, adminBranch: null });

  handleNewAdminChange = (e) => {
    const { id, value } = e.target;
    this.setState((st) => ({ newAdmin: { ...st.newAdmin, [id]: value } }));
  };

  submitBranchAdmin = () => {
    const { adminBranch, newAdmin } = this.state;
    const email = (newAdmin.adminEmail || "").trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid admin email");
      return;
    }
    this.setState({ savingAdmin: true });
    addBranchAdmin(adminBranch._id, { ...newAdmin, adminEmail: email }).then((res) => {
      if (res && res.status === 200) {
        // The message distinguishes "emailed" from "created but email failed" —
        // pass it through, because in the second case the generated password is
        // gone and the user has to reset it.
        toast.success(res.message);
        this.closeAdminModal();
        this.getAllBranches();
      } else {
        toast.error((res && res.message) || "Failed to create the branch admin");
        this.setState({ savingAdmin: false });
      }
    });
  };

  openSettings = (id) => {
    getBranchById(id)
      .then((result) => {
        if (result && result.status === 200) {
          const b = result.data.branch || result.data;
          const st = result.data.settings || {};
          this.setState({
            settingsModal: true,
            settingsBranchId: b._id,
            settingsBranchName: b.name,
            settings: {
              foodDeliveryCharge:
                st.foodDeliveryCharge != null ? String(st.foodDeliveryCharge) : "",
              minOrderAmount: st.minOrderAmount != null ? String(st.minOrderAmount) : "",
              taxRate: st.taxRate != null ? String(st.taxRate) : "",
              roomBookingEnabled: st.roomBookingEnabled !== false,
              tableBookingEnabled: st.tableBookingEnabled !== false,
              foodOrderingEnabled: st.foodOrderingEnabled !== false,
              transportEnabled: st.transportEnabled !== false,
              isActive: st.isActive !== false,
            },
          });
        } else {
          toast.error((result && result.message) || "Failed to load settings");
        }
      })
      .catch(() => toast.error("Error loading settings"));
  };

  handleSettingChange = (field, value) => {
    this.setState((s) => ({ settings: { ...s.settings, [field]: value } }));
  };

  handleSettingsSubmit = () => {
    const { settingsBranchId, settings } = this.state;
    const payload = {
      roomBookingEnabled: settings.roomBookingEnabled,
      tableBookingEnabled: settings.tableBookingEnabled,
      foodOrderingEnabled: settings.foodOrderingEnabled,
      transportEnabled: settings.transportEnabled,
      isActive: settings.isActive,
    };
    if (settings.foodDeliveryCharge !== "")
      payload.foodDeliveryCharge = Number(settings.foodDeliveryCharge);
    if (settings.minOrderAmount !== "")
      payload.minOrderAmount = Number(settings.minOrderAmount);
    if (settings.taxRate !== "") payload.taxRate = Number(settings.taxRate);

    this.setState({ settingsSaving: true });
    updateBranchSettings(settingsBranchId, payload)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Branch settings updated");
          this.setState({ settingsModal: false });
        } else {
          toast.error((response && response.message) || "Failed to update settings");
        }
      })
      .catch(() => toast.error("Error updating settings"))
      .finally(() => this.setState({ settingsSaving: false }));
  };

  // ---- Toggle / Delete ----
  toggleToggleModal = () => this.setState({ showToggleModal: !this.state.showToggleModal });
  toggleDeleteModal = () => this.setState({ showDeleteModal: !this.state.showDeleteModal });

  handleToggleStatus = (id, currentStatus) => {
    this.setState({ selectedBranchId: id, selectedBranchStatus: currentStatus, showToggleModal: true });
  };

  confirmToggleStatus = () => {
    const { selectedBranchId, selectedBranchStatus } = this.state;
    const action = selectedBranchStatus ? "deactivate" : "activate";
    this.setState({ actionLoading: true });
    toggleBranchStatus(selectedBranchId)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(`Branch ${action}d successfully`);
          this.getAllBranches();
        } else {
          toast.error((response && response.message) || `Failed to ${action} branch`);
        }
      })
      .catch(() => toast.error(`Error ${action}ing branch`))
      .finally(() =>
        this.setState({
          actionLoading: false,
          showToggleModal: false,
          selectedBranchId: null,
          selectedBranchStatus: null,
        })
      );
  };

  handleDelete = (id, isDefault) => {
    if (isDefault) {
      toast.error("The default branch cannot be deleted");
      return;
    }
    this.setState({ selectedBranchId: id, showDeleteModal: true });
  };

  confirmDelete = () => {
    const { selectedBranchId } = this.state;
    this.setState({ actionLoading: true });
    deleteBranch(selectedBranchId)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Branch deleted successfully");
          this.getAllBranches();
        } else {
          toast.error((response && response.message) || "Failed to delete branch");
        }
      })
      .catch(() => toast.error("Error deleting branch"))
      .finally(() =>
        this.setState({ actionLoading: false, showDeleteModal: false, selectedBranchId: null })
      );
  };

  // ------------------------------------------------------------------ modal UI

  renderCatalogSection = () => {
    const s = this.state;
    const searching = s.catalogSearch.trim() !== "";
    const totals = this.catalogTotals();

    return (
      <React.Fragment>
        <div style={{ fontSize: 13, color: C.taupe, lineHeight: 1.55 }}>
          Everything in the master catalog is carried by this branch by default — untick
          whatever it does not sell.
        </div>

        <div
          style={{
            marginTop: 14, marginBottom: 16, display: "flex", alignItems: "center", gap: 12,
            padding: 14, borderRadius: 14, background: C.surfaceAlt, border: `1px solid ${C.border}`,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Input
              type="text"
              value={s.catalogSearch}
              onChange={(e) => this.setState({ catalogSearch: e.target.value })}
              placeholder="Search catalog items by name…"
              disabled={s.catalogLoading || !s.catalogGroups.length}
            />
          </div>
          <Chip tone={totals.selected === totals.total ? "good" : "warn"}>
            {totals.selected} of {totals.total} carried
          </Chip>
        </div>

        {s.catalogLoading && (
          <div style={{ fontSize: 13, color: C.taupeLight }}>Loading master catalog…</div>
        )}

        {!s.catalogLoading && !s.catalogGroups.length && (
          <div style={{ fontSize: 13, color: C.taupeLight }}>No master catalog available.</div>
        )}

        {s.catalogGroups.map((g) => {
          const items = g.items || [];
          const visible = this.filteredItems(g);
          const selected = this.countSelected(g.key);
          const isEmpty = items.length === 0;
          const isOpen = searching ? visible.length > 0 : !!s.catalogOpenGroups[g.key];

          return (
            <div
              key={g.key}
              style={{
                marginBottom: 10, borderRadius: 14, overflow: "hidden",
                border: `1px solid ${C.border}`, background: "#fff",
                opacity: isEmpty ? 0.7 : 1,
              }}
            >
              <div
                onClick={() => {
                  if (!isEmpty && !searching) this.toggleCatalogGroup(g.key);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "11px 14px", background: C.surfaceAlt,
                  cursor: isEmpty || searching ? "default" : "pointer",
                }}
              >
                <span className="ms" style={{ fontSize: 20, color: C.taupe }}>
                  {isOpen ? "expand_more" : "chevron_right"}
                </span>
                <span style={{ flex: 1, fontSize: 15, color: C.ink }}>{g.label || g.key}</span>
                <Chip tone={isEmpty ? "neutral" : selected === items.length ? "good" : "warn"}>
                  {selected} of {items.length}
                </Chip>
                <button
                  type="button"
                  disabled={isEmpty || !visible.length}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setGroupSelection(g.key, true);
                  }}
                  style={{
                    border: 0, background: "transparent", color: C.brown, fontSize: 13,
                    fontFamily: "inherit", cursor: "pointer", padding: "0 2px",
                    opacity: isEmpty || !visible.length ? 0.45 : 1,
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  disabled={isEmpty || !visible.length}
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setGroupSelection(g.key, false);
                  }}
                  style={{
                    border: 0, background: "transparent", color: C.brown, fontSize: 13,
                    fontFamily: "inherit", cursor: "pointer", padding: "0 2px",
                    opacity: isEmpty || !visible.length ? 0.45 : 1,
                  }}
                >
                  None
                </button>
              </div>

              {isEmpty ? (
                <div style={{ padding: "12px 14px", fontSize: 13, color: C.taupeLight }}>
                  No items in this catalog yet.
                </div>
              ) : (
                <Collapse isOpen={isOpen}>
                  <div style={{ padding: "10px 14px", maxHeight: 230, overflowY: "auto" }}>
                    {visible.length === 0 ? (
                      <span style={{ fontSize: 13, color: C.taupeLight }}>
                        No items match “{s.catalogSearch}”.
                      </span>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                          gap: "2px 16px",
                        }}
                      >
                        {visible.map((it) => (
                          <label className="br-check" key={it._id}>
                            <input
                              type="checkbox"
                              checked={!!(s.catalogSelection[g.key] || {})[it._id]}
                              onChange={() => this.toggleCatalogItem(g.key, it._id)}
                            />
                            <span>
                              {it.name}
                              {/* price is an OBJECT from the API — never render it raw */}
                              {formatItemPrice(it.price) && (
                                <span style={{ color: C.taupeLight }}>
                                  {" "}
                                  · {formatItemPrice(it.price)}
                                </span>
                              )}
                              {it.isActive === false && (
                                <span style={{ color: C.red }}> · discontinued</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </Collapse>
              )}
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  // What the customer app shows on the branch's page: photos, check-in /
  // check-out times and the cancellation terms. The description lives on the
  // Details tab.
  renderStorefrontSection() {
    const s = this.state;
    return (
      <div>
        <div style={{ fontSize: 13, color: C.taupe, marginBottom: 14 }}>
          Shown to guests on this branch's page in the app. Images are links (http/https);
          paste the address of an image that is already hosted.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 14, alignItems: "end" }}>
          <Field label="Cover image">
            <Input
              id="coverImage"
              value={s.coverImage}
              onChange={this.handleChange}
              placeholder="https://…/cover.jpg"
            />
          </Field>
          <div style={{ height: 90, borderRadius: 12, overflow: "hidden", border: `1px solid ${C.tint}` }}>
            <Thumb src={s.coverImage.trim()} alt="Cover" icon="image" height={90} />
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <Field label={`Gallery${s.images.length ? ` (${s.images.length})` : ""}`}>
            <div style={{ display: "flex", gap: 10 }}>
              <Input
                id="newImageUrl"
                value={s.newImageUrl}
                onChange={this.handleChange}
                placeholder="https://…/photo.jpg"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    this.addGalleryImage();
                  }
                }}
              />
              <Btn kind="quiet" icon="add" onClick={this.addGalleryImage}>
                Add
              </Btn>
            </div>
          </Field>
          {s.images.length === 0 ? (
            <div style={{ marginTop: 10, fontSize: 13, color: C.taupeLight }}>No gallery images yet.</div>
          ) : (
            <div
              style={{
                marginTop: 12, display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10,
              }}
            >
              {s.images.map((url, i) => (
                <div
                  key={url}
                  style={{
                    position: "relative", height: 90, borderRadius: 12,
                    overflow: "hidden", border: `1px solid ${C.tint}`,
                  }}
                  title={url}
                >
                  <Thumb src={url} alt={`Gallery ${i + 1}`} icon="image" height={90} />
                  <button
                    type="button"
                    onClick={() => this.removeGalleryImage(i)}
                    aria-label="Remove image"
                    style={{
                      position: "absolute", top: 6, right: 6, width: 26, height: 26,
                      borderRadius: 13, border: 0, background: "rgba(43,32,24,.72)",
                      color: "#fff", display: "grid", placeItems: "center", cursor: "pointer",
                    }}
                  >
                    <span className="ms" style={{ fontSize: 16 }}>close</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <Field label="Check-in time">
            <Input type="time" id="checkInTime" value={s.checkInTime} onChange={this.handleChange} />
          </Field>
          <Field label="Check-out time">
            <Input type="time" id="checkOutTime" value={s.checkOutTime} onChange={this.handleChange} />
          </Field>
          <Field label="Free cancellation (hours)" hint="Before arrival. Blank = not offered.">
            <Input
              type="number"
              id="freeCancelHours"
              min="0"
              step="1"
              value={s.freeCancelHours}
              onChange={this.handleChange}
              placeholder="24"
            />
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field label="Cancellation policy">
            <Input
              type="textarea"
              id="cancellationPolicy"
              value={s.cancellationPolicy}
              onChange={this.handleChange}
              rows={3}
              placeholder="e.g. Free cancellation up to 24 hours before arrival; after that the first night is charged."
            />
          </Field>
        </div>
      </div>
    );
  }

  renderFormModal() {
    const s = this.state;
    const tabs = this.tabsForModal();

    return (
      <Modal isOpen={s.modal} toggle={this.toggle} size="lg" className="br-modal con-modal">
        <div style={{ padding: 30 }}>
          <DialogHead
            title={s.branchId ? "Edit branch" : "Add branch"}
            subtitle={
              s.branchId
                ? "Changes take effect for this branch as soon as you save"
                : "A branch gets its own storefront, catalog and, optionally, its own manager"
            }
            onClose={this.toggle}
          />

          <div style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`br-tab ${s.activeTab === t.key ? "is-active" : ""}`}
                onClick={() => this.setState({ activeTab: t.key })}
              >
                <span className="ms" style={{ fontSize: 18 }}>{t.icon}</span>
                {t.label}
                {t.badge != null && (
                  <span
                    style={{
                      fontSize: 12, padding: "2px 8px", borderRadius: 9,
                      background: s.activeTab === t.key ? "rgba(255,255,255,.18)" : C.tint,
                      color: s.activeTab === t.key ? "#fff" : C.brown,
                    }}
                  >
                    {t.badge}
                  </span>
                )}
                {t.hint && (
                  <span
                    style={{
                      fontSize: 12, padding: "2px 8px", borderRadius: 9,
                      background: s.activeTab === t.key ? "rgba(255,255,255,.18)" : C.tint,
                      color: s.activeTab === t.key ? "#fff" : C.taupe,
                    }}
                  >
                    {t.hint}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 20 }}>
            <TabContent activeTab={s.activeTab}>
              <TabPane tabId="details">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14 }}>
                  <Field label="Code *">
                    <Input
                      id="code"
                      value={s.code}
                      onChange={this.handleChange}
                      placeholder="MAIN"
                      style={{ textTransform: "uppercase" }}
                    />
                  </Field>
                  <Field label="Name *">
                    <Input
                      id="name"
                      value={s.name}
                      onChange={this.handleChange}
                      placeholder="Branch name"
                    />
                  </Field>
                </div>

                <div style={{ marginTop: 14 }}>
                  <Field label="Description">
                    <Input
                      type="textarea"
                      id="description"
                      value={s.description}
                      onChange={this.handleChange}
                      rows={2}
                    />
                  </Field>
                </div>

                <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 14 }}>
                  <Field label="Address">
                    <Input id="address" value={s.address} onChange={this.handleChange} />
                  </Field>
                  <Field label="City">
                    <Input id="city" value={s.city} onChange={this.handleChange} />
                  </Field>
                  <Field label="Country">
                    <Input type="select" id="country" value={s.country} onChange={this.handleChange}>
                      {/* Preserve a value saved before this became a fixed list,
                          rather than silently switching the branch on save. */}
                      {s.country && !COUNTRIES.includes(s.country) && (
                        <option value={s.country}>{s.country}</option>
                      )}
                      {COUNTRIES.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </Input>
                  </Field>
                </div>

                <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                  <Field label="Latitude">
                    <Input
                      type="number"
                      id="latitude"
                      value={s.latitude}
                      onChange={this.handleChange}
                      placeholder="-11.6647"
                    />
                  </Field>
                  <Field label="Longitude">
                    <Input
                      type="number"
                      id="longitude"
                      value={s.longitude}
                      onChange={this.handleChange}
                      placeholder="27.4794"
                    />
                  </Field>
                  <Field label="Timezone">
                    <Input type="select" id="timezone" value={s.timezone} onChange={this.handleChange}>
                      {/* Preserve a value saved before this became a fixed list,
                          rather than silently switching the branch on save. */}
                      {s.timezone && !TIMEZONES.includes(s.timezone) && (
                        <option value={s.timezone}>{s.timezone}</option>
                      )}
                      {TIMEZONES.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </Input>
                  </Field>
                  <Field label="Currency">
                    <Input type="select" id="currency" value={s.currency} onChange={this.handleChange}>
                      {/* A branch saved before this was a fixed list may hold a
                          code that is not in it. Show it rather than silently
                          switching the branch to USD on the next save. */}
                      {s.currency && !CURRENCIES.some((c) => c.code === s.currency) && (
                        <option value={s.currency}>{s.currency}</option>
                      )}
                      {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code} — {c.label}
                        </option>
                      ))}
                    </Input>
                  </Field>
                </div>

                <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Field label="Contact phone">
                    <Input id="contactPhone" value={s.contactPhone} onChange={this.handleChange} />
                  </Field>
                  {/* This is the branch's public contact address. It is easy to
                      assume it creates a manager login — it does not; that is
                      the Branch Admin tab. */}
                  <Field
                    label="Contact email"
                    hint="Public contact for the branch. This does not create a login — use the Branch admin tab for that."
                  >
                    <Input
                      type="email"
                      id="contactEmail"
                      value={s.contactEmail}
                      onChange={this.handleChange}
                    />
                  </Field>
                </div>
              </TabPane>

              <TabPane tabId="hours">
                <div style={{ fontSize: 13, color: C.taupe, marginBottom: 14 }}>
                  When this branch takes orders and bookings. Times are in the branch's own
                  timezone.
                </div>
                {s.operatingHours.map((h, i) => (
                  <div
                    key={h.day}
                    style={{
                      display: "grid", gridTemplateColumns: "1.1fr 1fr 1fr 1fr",
                      alignItems: "center", gap: 12, padding: "9px 0",
                      borderBottom: i === s.operatingHours.length - 1 ? "none" : `1px solid ${C.tint}`,
                      opacity: h.isClosed ? 0.6 : 1,
                    }}
                  >
                    <span style={{ fontSize: 15, color: C.ink }}>{DAYS[i].label}</span>
                    <Input
                      type="time"
                      value={h.openTime}
                      disabled={h.isClosed}
                      onChange={(e) => this.handleHourChange(i, "openTime", e.target.value)}
                    />
                    <Input
                      type="time"
                      value={h.closeTime}
                      disabled={h.isClosed}
                      onChange={(e) => this.handleHourChange(i, "closeTime", e.target.value)}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Toggle
                        on={h.isClosed}
                        title={`${DAYS[i].label} closed`}
                        onClick={() => this.handleHourChange(i, "isClosed", !h.isClosed)}
                      />
                      <span style={{ fontSize: 14, color: C.taupe }}>
                        {h.isClosed ? "Closed" : "Open"}
                      </span>
                    </div>
                  </div>
                ))}
              </TabPane>

              <TabPane tabId="storefront">{this.renderStorefrontSection()}</TabPane>

              <TabPane tabId="admin">
                {!s.branchId && (
                  <React.Fragment>
                    <div style={{ fontSize: 13, color: C.taupe, lineHeight: 1.55, marginBottom: 16 }}>
                      Optional. Enter an email to provision a dedicated admin for this branch —
                      they receive auto-generated login credentials by email and manage only this
                      branch.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <Field label="Admin email">
                        <Input
                          type="email"
                          id="adminEmail"
                          value={s.adminEmail}
                          onChange={this.handleChange}
                          placeholder="branch.admin@example.com"
                        />
                      </Field>
                      <Field label="Admin mobile">
                        <Input
                          type="number"
                          id="adminMobile"
                          value={s.adminMobile}
                          onChange={this.handleChange}
                          placeholder="243800000000"
                        />
                      </Field>
                      <Field label="Admin first name">
                        <Input
                          id="adminFirstName"
                          value={s.adminFirstName}
                          onChange={this.handleChange}
                        />
                      </Field>
                      <Field label="Admin last name">
                        <Input
                          id="adminLastName"
                          value={s.adminLastName}
                          onChange={this.handleChange}
                        />
                      </Field>
                    </div>
                  </React.Fragment>
                )}
              </TabPane>

              <TabPane tabId="catalog">{this.renderCatalogSection()}</TabPane>
            </TabContent>
          </div>

          <div
            style={{
              marginTop: 26, display: "flex", alignItems: "center",
              justifyContent: "space-between", gap: 12,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <Btn
                kind="quiet"
                icon="arrow_back"
                disabled={this.tabIndex() === 0}
                onClick={() => this.goStep(-1)}
              >
                Back
              </Btn>
              <Btn
                kind="quiet"
                disabled={this.tabIndex() >= tabs.length - 1}
                onClick={() => this.goStep(1)}
              >
                Next
              </Btn>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn kind="quiet" onClick={this.toggle}>Cancel</Btn>
              {canWrite() && (
                <Btn icon="save" onClick={this.handleSubmit} disabled={s.saving}>
                  {s.saving ? "Saving…" : s.branchId ? "Update branch" : "Create branch"}
                </Btn>
              )}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  renderAdminModal() {
    const s = this.state;
    return (
      <Modal isOpen={s.adminModal} toggle={this.closeAdminModal} className="br-modal con-modal" centered>
        <div style={{ padding: 30 }}>
          <DialogHead
            title="Add branch admin"
            subtitle={
              s.adminBranch
                ? `Manages ${s.adminBranch.name} and nothing else`
                : "Manages this branch and nothing else"
            }
            onClose={this.closeAdminModal}
          />

          <div
            style={{
              marginTop: 18, padding: "14px 16px", borderRadius: 14,
              background: C.surfaceAlt, fontSize: 13, color: C.taupe, lineHeight: 1.55,
            }}
          >
            A password is generated and emailed to them — it is never shown here, so if the
            email fails you will need to reset it under Team.
          </div>

          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Admin email *">
              <Input
                type="email"
                id="adminEmail"
                value={s.newAdmin.adminEmail}
                onChange={this.handleNewAdminChange}
                placeholder="branch.admin@example.com"
              />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label="First name">
                <Input
                  id="adminFirstName"
                  value={s.newAdmin.adminFirstName}
                  onChange={this.handleNewAdminChange}
                />
              </Field>
              <Field label="Last name">
                <Input
                  id="adminLastName"
                  value={s.newAdmin.adminLastName}
                  onChange={this.handleNewAdminChange}
                />
              </Field>
            </div>
            <Field label="Mobile">
              <Input
                id="adminMobile"
                value={s.newAdmin.adminMobile}
                onChange={this.handleNewAdminChange}
                placeholder="243800000000"
              />
            </Field>
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn kind="quiet" onClick={this.closeAdminModal}>Cancel</Btn>
            {canWrite() && (
              <Btn icon="mail" onClick={this.submitBranchAdmin} disabled={s.savingAdmin}>
                {s.savingAdmin ? "Creating…" : "Create & email credentials"}
              </Btn>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  renderSettingsModal() {
    const s = this.state;
    const services = [
      { key: "foodOrderingEnabled", label: "Food ordering", icon: "restaurant" },
      { key: "tableBookingEnabled", label: "Table booking", icon: "table_restaurant" },
      { key: "roomBookingEnabled", label: "Room booking", icon: "hotel" },
      { key: "transportEnabled", label: "Transport", icon: "local_taxi" },
    ];

    return (
      <Modal isOpen={s.settingsModal} toggle={this.toggleSettings} className="br-modal con-modal" centered>
        <div style={{ padding: 30 }}>
          <DialogHead
            title="Branch settings"
            subtitle={s.settingsBranchName}
            onClose={this.toggleSettings}
          />

          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
            <Field label="Delivery charge">
              <Input
                type="number"
                value={s.settings.foodDeliveryCharge}
                onChange={(e) => this.handleSettingChange("foodDeliveryCharge", e.target.value)}
              />
            </Field>
            <Field label="Min order">
              <Input
                type="number"
                value={s.settings.minOrderAmount}
                onChange={(e) => this.handleSettingChange("minOrderAmount", e.target.value)}
              />
            </Field>
            <Field label="Tax rate (%)">
              <Input
                type="number"
                value={s.settings.taxRate}
                onChange={(e) => this.handleSettingChange("taxRate", e.target.value)}
              />
            </Field>
          </div>

          <div style={{ marginTop: 22, fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupe }}>
            Services this branch offers
          </div>
          <div style={{ marginTop: 6 }}>
            {services.map((opt, i) => (
              <div
                key={opt.key}
                style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 0",
                  borderBottom: i === services.length - 1 ? "none" : `1px solid ${C.tint}`,
                }}
              >
                <span
                  className="ms"
                  style={{ fontSize: 19, color: C.brown, background: C.iconTint, padding: 7, borderRadius: 10 }}
                >
                  {opt.icon}
                </span>
                <span style={{ flex: 1, fontSize: 15, color: C.ink }}>{opt.label}</span>
                <Toggle
                  on={s.settings[opt.key]}
                  title={opt.label}
                  onClick={() => this.handleSettingChange(opt.key, !s.settings[opt.key])}
                />
              </div>
            ))}
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn kind="quiet" onClick={this.toggleSettings}>Cancel</Btn>
            {canWrite() && (
              <Btn icon="save" onClick={this.handleSettingsSubmit} disabled={s.settingsSaving}>
                {s.settingsSaving ? "Saving…" : "Save settings"}
              </Btn>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    const s = this.state;
    const rows = this.visibleBranches();
    const active = s.items.filter((b) => b.isActive).length;
    const inactive = s.items.length - active;
    const cities = new Set(s.items.map((b) => b.city).filter(Boolean)).size;

    return (
      <ConsolePage
        title="Branches"
        subtitle={
          s.isLoaded
            ? `${s.items.length} branch${s.items.length === 1 ? "" : "es"} in this hotel`
            : "Loading branches…"
        }
        actions={
          <React.Fragment>
            <SearchPill
              value={s.search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Code, name or city"
              width={230}
            />
            <Btn
              kind="quiet"
              icon="inventory_2"
              onClick={() => this.props.history.push("/master-catalog")}
            >
              Master catalog
            </Btn>
            {canWrite() && (
              <Btn icon="add" onClick={this.openCreate}>
                Add branch
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <StatCard icon="apartment" value={s.items.length} label="Branches" />
            <StatCard icon="check_circle" value={active} label="Taking orders" />
            <StatCard icon="pause_circle" value={inactive} label="Deactivated" />
            <StatCard icon="location_city" value={cities} label={`Cit${cities === 1 ? "y" : "ies"} covered`} />
          </StatRow>

          <Panel style={{ padding: 0 }}>
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "16px 22px", borderBottom: `1px solid ${C.panel}`,
              }}
            >
              <div>
                <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>All branches</div>
                <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight }}>
                  Each branch has its own storefront, catalog selection and settings
                </div>
              </div>
              <div style={{ fontSize: 14, color: C.taupeLight }}>
                {rows.length} of {s.items.length} shown
              </div>
            </div>

            <div style={{ padding: "0 10px" }}>
              {!s.isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading branches…
                </div>
              ) : (
                <Table
                  columns={this.columns()}
                  rows={rows}
                  empty={
                    s.items.length === 0
                      ? "No branches yet — add the first one to open a storefront"
                      : "No branch matches this search"
                  }
                />
              )}
            </div>
          </Panel>
        </div>

        {this.renderFormModal()}
        {this.renderAdminModal()}
        {this.renderSettingsModal()}

        {/* Toggle Status Confirmation */}
        <ConfirmDialog
          isOpen={s.showToggleModal}
          toggle={this.toggleToggleModal}
          onConfirm={this.confirmToggleStatus}
          icon={s.selectedBranchStatus ? "pause_circle" : "play_circle"}
          tone={s.selectedBranchStatus ? "danger" : "neutral"}
          title={s.selectedBranchStatus ? "Deactivate this branch?" : "Activate this branch?"}
          body={
            s.selectedBranchStatus
              ? "Guests will no longer see it or be able to order from it. Nothing is deleted — you can activate it again at any time."
              : "The branch will appear to guests again and can take orders and bookings."
          }
          confirmText={s.selectedBranchStatus ? "Deactivate" : "Activate"}
          loading={s.actionLoading}
        />

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={s.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={this.confirmDelete}
          icon="delete"
          title="Delete this branch?"
          body="This cannot be undone."
          tip="If you only want to stop taking orders here, deactivate the branch instead — that is reversible and keeps its history intact."
          confirmText="Delete branch"
          loading={s.actionLoading}
        />
      </ConsolePage>
    );
  }
}

export default BranchList;
